// /home/ubuntu/my-wallboard/src/MuiStyleTable.jsx

import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@/components/ui/table";

export default function WallboardPage() {
  console.log("✅ MuiStyleTable mounted");

  const { slug } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [rows, setRows] = useState([]);
  const [authenticated, setAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [tenantName, setTenantName] = useState(slug);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [isLoadingAuth, setIsLoadingAuth] = useState(false);
  const [error, setError] = useState("");
  const [theme, setTheme] = useState("light");
  const wsRef = useRef(null);

  const isDark = theme === "dark";

  // Persist login on reload
  useEffect(() => {
    if (sessionStorage.getItem("authenticated") === slug) {
      setAuthenticated(true);
    }
  }, [slug]);

  // Fetch tenant config
  useEffect(() => {
    axios
      .get(`/tenant/${slug}`)
      .then((res) => {
        setConfig(res.data);
        setTenantName(res.data.slug || slug);
      })
      .catch(() => {
        setError("Tenant not found");
      })
      .finally(() => {
        setIsLoadingConfig(false);
      });
  }, [slug]);

  // Apply theme classes
  useEffect(() => {
    document.body.classList.toggle("bg-gray-900", isDark);
    document.body.classList.toggle("text-white", isDark);
    document.body.classList.toggle("bg-gray-50", !isDark);
    document.body.classList.toggle("text-gray-900", !isDark);
  }, [isDark]);

  // Open WebSocket after login
  useEffect(() => {
    if (!authenticated || !config) return;
    const ws = new WebSocket(
      `wss://${window.location.host}/ws/${slug}/${config.api_key}`
    );
    wsRef.current = ws;

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        setRows(
          data.map((item) => ({
            number: item.Number,
            name: item.Name || "—",
            callsThisHour: item.CallsThisHour || 0,
            callsToday: item.CallsToday || 0,
            callsThisWeek: item.CallsThisWeek || 0,
            callsThisMonth: item.CallsThisMonth || 0,
          }))
        );
      } catch (err) {
        console.error("Error parsing WS message:", err);
      }
    };
    ws.onerror = () => {
      toast.error("WebSocket connection error");
      sessionStorage.removeItem("authenticated");
      setAuthenticated(false);
    };
    ws.onclose = () => {
      console.warn("WebSocket closed");
      sessionStorage.removeItem("authenticated");
      setAuthenticated(false);
    };
    return () => ws.close();
  }, [authenticated, config, slug]);

  const handleLogin = async () => {
    setError("");
    setIsLoadingAuth(true);
    try {
      const res = await axios.post(
        "/auth",
        { slug, password: passwordInput },
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.data.success) {
        sessionStorage.setItem("authenticated", slug);
        setAuthenticated(true);
      } else {
        setError("Incorrect password");
      }
    } catch (err) {
      console.error("Auth error:", err);
      setError("Error contacting server");
    } finally {
      setIsLoadingAuth(false);
    }
  };

  const handleLogout = () => {
    if (wsRef.current) wsRef.current.close();
    sessionStorage.removeItem("authenticated");
    setAuthenticated(false);
    setRows([]);
    setPasswordInput("");
  };

  if (isLoadingConfig) return <p className="p-4">Loading tenant…</p>;
  if (error && !authenticated) return <p className="p-4 text-red-500">{error}</p>;

  // Login form
  if (!authenticated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-md p-6 space-y-4">
          <h2 className="text-2xl font-bold text-center">Login to {tenantName}</h2>
          <input
            type="password"
            placeholder="Password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            className="w-full p-2 border rounded"
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          />
          <Button onClick={handleLogin} disabled={isLoadingAuth} className="w-full flex items-center justify-center">
            {isLoadingAuth ? "…Logging in" : <><Bell className="mr-2" />Enter</>}
          </Button>
          {error && <p className="text-red-500">{error}</p>}
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between p-4 shadow">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-sm opacity-75">Live Call Statistics for {tenantName}</p>
        </div>
        <div className="flex items-center space-x-4">
          <Button variant="outline" size="icon" onClick={() => toast("🔔")}> <Bell className="w-5 h-5" /> </Button>
          <Button onClick={() => setTheme(isDark ? "light" : "dark")}>Toggle {isDark ? "Light" : "Dark"}</Button>
          <Button variant="destructive" onClick={handleLogout}>Logout</Button>
        </div>
      </header>
      <main className="flex-1 p-4">
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Number</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Calls This Hour</TableCell>
                <TableCell>Calls Today</TableCell>
                <TableCell>Calls This Week</TableCell>
                <TableCell>Calls This Month</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i} className={i % 2 ? "bg-gray-100" : ""}>
                  <TableCell>{row.number}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.callsThisHour}</TableCell>
                  <TableCell>{row.callsToday}</TableCell>
                  <TableCell>{row.callsThisWeek}</TableCell>
                  <TableCell>{row.callsThisMonth}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </main>
    </div>
  );
}

